import { ChevronDown, Landmark, PiggyBank } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";

import { OrderFormData } from "../OrderForm.schema";
import { SavingsAgreementSection } from "./SavingsAgreementSection";
import { WithdrawalAgreementSection } from "./WithdrawalAgreementSection";
import { QuerySecurity } from "@/api/queries/types";
import { Portfolio } from "@/api/types";

type Props = {
  securities?: QuerySecurity[];
  allPortfolios: Portfolio[];
};

export function AgreementsGroup({ securities = [], allPortfolios }: Props) {
  const { setValue } = useFormContext<OrderFormData>();

  const savingsEnabled = useWatch({ name: "dpSavingsEnabled" }) as boolean;
  const withdrawalEnabled = useWatch({
    name: "dpWithdrawalEnabled",
  }) as boolean;
  const modelType = useWatch({ name: "dpModelType" }) as string | undefined;
  const hasModel = !!modelType && modelType !== "";

  const toggleSavings = () => {
    const next = !savingsEnabled;
    setValue("dpSavingsEnabled", next, { shouldValidate: true });
    if (!next) {
      setValue("dpSavingsAmount", undefined);
      setValue("dpSavingsDayOfMonth", undefined);
      setValue("dpSavingsAvtalegiro", false);
      setValue("dpSavingsFunds", []);
    }
  };

  const toggleWithdrawal = () => {
    const next = !withdrawalEnabled;
    setValue("dpWithdrawalEnabled", next, { shouldValidate: true });
    if (!next) {
      setValue("dpWdAccount", "");
      setValue("dpWdDayOfMonth", undefined);
      setValue("dpWdAmount", undefined);
    }
  };

  const disabledCls =
    "border-dashed border-gray-200 bg-transparent text-gray-300 cursor-not-allowed dark:border-gray-700 dark:text-gray-600";
  const inactiveCls =
    "border-dashed border-gray-300 bg-transparent text-gray-500 hover:border-gray-400 hover:text-gray-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500";

  return (
    <div className="mt-2">
      {/* Side-by-side toggle buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={toggleSavings}
          disabled={!hasModel}
          title={
            !hasModel
              ? "Select a model portfolio to enable savings agreement"
              : undefined
          }
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors
            ${
              !hasModel
                ? disabledCls
                : savingsEnabled
                ? "border-emerald-400 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                : inactiveCls
            }`}
        >
          <span className="flex items-center gap-2">
            <PiggyBank className="h-4 w-4 flex-shrink-0" />
            Savings agreement
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              savingsEnabled ? "rotate-180" : ""
            }`}
          />
        </button>

        <button
          type="button"
          onClick={toggleWithdrawal}
          disabled={!hasModel}
          title={
            !hasModel
              ? "Select a model portfolio to enable withdrawal agreement"
              : undefined
          }
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors
            ${
              !hasModel
                ? disabledCls
                : withdrawalEnabled
                ? "border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                : inactiveCls
            }`}
        >
          <span className="flex items-center gap-2">
            <Landmark className="h-4 w-4 flex-shrink-0" />
            Withdrawal agreement
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              withdrawalEnabled ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Full-width expanded bodies */}
      <SavingsAgreementSection hideToggle securities={securities} />
      <WithdrawalAgreementSection hideToggle allPortfolios={allPortfolios} />
    </div>
  );
}
